import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 天然气制烯烃（GTO）P&ID 详细图：蒸汽转化 → 甲醇合成 → MTO → 分离 */
const media = {
  gas: { label: '天然气 / 烯烃气', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  syngas: { label: '合成气', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  meth: { label: '甲醇', light: '#00838f', dark: '#4dd0e1', width: 2.6 },
  cat: { label: '催化剂（循环输送）', light: '#8e24aa', dark: '#ce93d8', width: 3.2, dash: '12 5' },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  water: { label: '水 / 急冷水', light: '#1565c0', dark: '#64b5f6', width: 2 },
};

const devices = [
  { id: 'f101', kind: 'furnace' as const, x: 120, y: 146, tag: 'F-101', name: '蒸汽转化炉', note: '850℃' },
  { id: 'r101', kind: 'reactor' as const, x: 310, y: 128, tag: 'R-101', name: '甲醇合成塔' },
  { id: 'v101', kind: 'vDrum' as const, x: 480, y: 161, tag: 'V-101', name: '甲醇分离器' },
  { id: 'r102', kind: 'reactor' as const, x: 640, y: 128, tag: 'R-102', name: 'MTO反应器', note: '流化床' },
  { id: 'd101', kind: 'vDrum' as const, x: 810, y: 161, tag: 'D-101', name: '沉降器' },
  { id: 'rg101', kind: 'regenerator' as const, x: 583, y: 430, tag: 'RG-101', name: '再生器' },
  { id: 't101', kind: 'column' as const, x: 980, y: 101, tag: 'T-101', name: '急冷塔' },
  { id: 'c101', kind: 'compressor' as const, x: 1140, y: 193, tag: 'C-101', name: '油气压缩机' },
  { id: 't102', kind: 'column' as const, x: 1290, y: 101, tag: 'T-102', name: '乙烯精馏塔' },
  { id: 't103', kind: 'column' as const, x: 1430, y: 101, tag: 'T-103', name: '丙烯精馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-gasin', label: '天然气', medium: 'gas', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-stm', label: '蒸汽', medium: 'steam', x: 60, y: 40, direction: 'in' as const },
  { id: 'p-air', label: '主风', medium: 'water', x: 460, y: 567, direction: 'in' as const },
  { id: 'p-ethylene', label: '乙烯', medium: 'gas', x: 1590, y: 611, direction: 'out' as const },
  { id: 'p-propylene', label: '丙烯', medium: 'meth', x: 1590, y: 681, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-gasin', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'gas' },
  { id: 's02', from: 'p-stm', fromSide: 'right', to: 'f101', toSide: 'top', medium: 'steam' },
  { id: 's03', from: 'f101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'syngas' },
  { id: 's04', from: 'r101', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'meth' },
  { id: 's05', from: 'v101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'meth' },
  { id: 's06', from: 'r102', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'gas' },
  { id: 's07', from: 'd101', fromSide: 'top', to: 't101', toSide: 'left', medium: 'gas' },
  { id: 's08', from: 't101', fromSide: 'right', to: 'c101', toSide: 'left', medium: 'gas' },
  { id: 's09', from: 'c101', fromSide: 'right', to: 't102', toSide: 'left', medium: 'gas' },
  { id: 's10', from: 't102', fromSide: 'right', to: 't103', toSide: 'left', medium: 'gas' },
  { id: 's11', from: 't102', fromSide: 'bottom', to: 'p-ethylene', toSide: 'left', medium: 'gas' },
  { id: 's12', from: 't103', fromSide: 'bottom', to: 'p-propylene', toSide: 'left', medium: 'meth' },
  { id: 's13', from: 'd101', fromSide: 'bottom', to: 'rg101', toSide: 'right', medium: 'cat' },
  { id: 's14', from: 'rg101', fromSide: 'top', to: 'r102', toSide: 'bottom', medium: 'cat' },
  { id: 's15', from: 'p-air', fromSide: 'right', to: 'rg101', toSide: 'left', medium: 'water' },
];

const GtoDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="GTO 装置详细流程"
    subtitle="天然气蒸汽转化 → 合成气 → 甲醇合成/分离 → MTO 流化床反应器（催化剂循环再生）→ 急冷压缩 → 乙烯/丙烯精馏（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default GtoDiagram;
